/*
 * <vitesse-widget-install-dialog> — "download this marketplace widget ONTO a
 * dashboard": pick which board, and the widget lands on it as a new tile.
 *
 * The marketplace panel (base-ui, app-agnostic) dispatches the widget; the shell
 * opens this, and on a pick calls DashboardManager.addTile — the SAME read-body
 * → append → save the AI's dashboard-designer uses. Lists every dashboard the
 * org has, grouped by relay when there is more than one, so a widget can go on
 * any board regardless of which gateway owns it.
 */
import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-dialog';
import type { GatewayEntry } from '../managers/gateway-entry';

/** The widget being installed (carried from the marketplace event). */
export interface InstallWidget {
  id: string;
  title: string;
  source: string;
  config: Record<string, unknown>;
}

@customElement('vitesse-widget-install-dialog')
export class VitesseWidgetInstallDialog extends LitElement {
  @property({ attribute: false }) widget?: InstallWidget;
  @property({ attribute: false }) entries: GatewayEntry[] = [];
  /** The gateway the person is looking at: its boards are listed first, since the board they
   *  want is almost always the one on screen. */
  @property() currentGateway = '';
  /** Set while a chosen board is being written, so the row shows progress and a
   *  second click can't double-install. */
  @state() private busy = '';
  @state() private error = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  }

  private async pick(entry: GatewayEntry, dashboardId: string): Promise<void> {
    if (!this.widget || this.busy) return;
    this.busy = dashboardId;
    this.error = '';
    // The SAME install the AI performs: append a tile + save (via '' → the user).
    const r = await entry.dashboards.addTile(
      dashboardId,
      { id: this.widget.id, source: this.widget.source, config: this.widget.config, title: this.widget.title },
      '',
    );
    this.busy = '';
    if (!r.ok) {
      this.error = r.error;
      return;
    }
    // Land the person ON the board they just added to, so they see it immediately.
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'dashboard', gatewayId: entry.gateway.deviceFingerprint, dashboardId },
        bubbles: true,
        composed: true,
      }),
    );
    this.close();
  }

  private renderRows(): TemplateResult {
    const multiGw = this.entries.length > 1;
    const groups = [...this.entries]
      .sort((a, b) => Number(b.gateway.deviceFingerprint === this.currentGateway) - Number(a.gateway.deviceFingerprint === this.currentGateway))
      .map((e) => ({ e, boards: e.dashboards.all }))
      .filter((g) => g.boards.length > 0);
    if (groups.length === 0) {
      return html`<div class="widget-install-empty">${t('marketplace.installNoBoards')}</div>`;
    }
    return html`${groups.map(
      ({ e, boards }) => html`
        ${multiGw ? html`<div class="widget-install-group">${e.gateway.displayName || e.gateway.deviceFingerprint.slice(0, 8)}</div>` : nothing}
        ${boards.map(
          (b) => html`<button
            class="widget-install-row ${this.busy === b.id ? 'is-busy' : ''}"
            ?disabled=${!!this.busy}
            @click=${() => void this.pick(e, b.id)}
          >
            <vitesse-icon name="layout-dashboard" size="15"></vitesse-icon>
            <span class="widget-install-row-title">${b.title}</span>
            <span class="widget-install-row-meta">${t('dashboards.tileCount', { count: b.tileCount })}</span>
          </button>`,
        )}
      `,
    )}`;
  }

  render(): TemplateResult {
    return html`<vitesse-dialog
      .open=${!!this.widget}
      heading=${t('marketplace.installHeading', { widget: this.widget?.title ?? '' })}
      @dialog-close=${() => this.close()}
    >
      <div class="widget-install-list">${this.renderRows()}</div>
      ${this.error ? html`<div class="widget-install-error">${this.error}</div>` : nothing}
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-widget-install-dialog': VitesseWidgetInstallDialog;
  }
}
